import { useState } from 'react';
import { formatCopy } from '@/i18n/ns';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/ui/kit';
import { AppDialog } from '../lifecycle/app-dialog';
import type { FindingsCopy } from './findings-copy';
import {
  chatNoticeFor,
  type ChatNotice,
  presentFindings,
  type CheckFinding,
  type CheckPresentation,
  type CheckReport,
  type DependencyFinding,
  type FindingRow,
  type FindingSeverity,
} from './findings-model';
import './findings.css';

/**
 * The callbacks are the host's presentation hooks; none is an "approved" signal and none is invented here.
 * The host sends the server only what it captured for this report (artifact/version/check ids, the finding
 * id, an operation id): never this module's labels, files or any scanner field as repair authority.
 */
export type FindingsHandlers = {
  /**
   * Starts the host's fix request (the user's request IS the build approval; no second tap). OPTIONAL: while
   * the host has no repair primitive the action is simply not shown, never a handler that pretends success.
   */
  onFix?: (finding: CheckFinding) => void;
  /** Optional like the others: absent, "Check again" is not shown. */
  onCheckAgain?: () => void;
  /**
   * Called ONLY after the confirm sheet, with the finding and the server's opaque ref of the report that was
   * shown (never resolved against a later report). Absent: the action is not offered at all (no genuine
   * acknowledgment port delivered yet).
   */
  onPublishAnyway?: (acknowledgment: { findingId: string; reportRef: string }) => void;
  /** Absent until a host Checks surface exists: Review is then not shown. */
  onReview?: () => void;
};

const TONE = {
  critical: 'danger',
  high: 'danger',
  unknown: 'danger',
  moderate: 'warning',
  low: 'warning',
} as const;
const TAG = (copy: FindingsCopy): Record<FindingSeverity, string> => ({
  critical: copy.tagCritical,
  high: copy.tagHigh,
  moderate: copy.tagModerate,
  low: copy.tagLow,
  unknown: copy.tagUnrated,
});
const WORD = (copy: FindingsCopy): Record<Exclude<FindingSeverity, 'unknown'>, string> => ({
  critical: copy.severityWordCritical,
  high: copy.severityWordHigh,
  moderate: copy.severityWordModerate,
  low: copy.severityWordLow,
});
const SECRET_TITLE = (copy: FindingsCopy) => ({
  api_key: copy.secretTitleApiKey,
  private_key: copy.secretTitlePrivateKey,
  access_token: copy.secretTitleAccessToken,
  password: copy.secretTitlePassword,
});

export function summaryText(copy: FindingsCopy, presentation: CheckPresentation): string | null {
  const { kind, count } = presentation.summary;
  const text = {
    checking: copy.summaryChecking,
    clean: copy.summaryClean,
    warnings: copy.summaryWarnings(count),
    'held-secret': copy.summaryHeldSecret,
    'held-dependency': copy.summaryHeldDependency,
    'held-many': copy.summaryHeldMany(count),
    expired: copy.summaryExpired,
    incomplete: copy.summaryIncomplete,
    'held-private': copy.summaryHeldPrivate,
    hidden: null,
  } as const;
  return text[kind];
}

/** The summary line (role=status) with its primary action; the Publish step shows only this. */
export function FindingsSummary({
  copy,
  report,
  handlers,
}: {
  copy: FindingsCopy;
  report: CheckReport;
  handlers: Pick<FindingsHandlers, 'onCheckAgain' | 'onReview'>;
}) {
  const presentation = presentFindings(report);
  const text = summaryText(copy, presentation);
  if (text === null) return null;
  const held = ['held-secret', 'held-dependency', 'held-many', 'held-private'].includes(
    presentation.summary.kind,
  );
  return (
    <div
      className="pc-findings-summary"
      role="status"
      data-testid="findings-summary"
      data-kind={presentation.summary.kind}
      data-held={held ? 'true' : undefined}
    >
      <p>{text}</p>
      {presentation.primary === 'check-again' && handlers.onCheckAgain && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="findings-check-again"
          onClick={handlers.onCheckAgain}
        >
          {copy.actionCheckAgain}
        </Button>
      )}
      {(presentation.summary.kind === 'warnings' || held) &&
        presentation.summary.kind !== 'held-private' &&
        handlers.onReview && (
          <Button type="button" variant="ghost" size="sm" onClick={handlers.onReview}>
            {copy.actionReview}
          </Button>
        )}
    </div>
  );
}

function severityWord(copy: FindingsCopy, finding: DependencyFinding) {
  return finding.severity === 'unknown' ? '' : WORD(copy)[finding.severity];
}

function Row({
  copy,
  row,
  handlers,
  onAnyway,
}: {
  copy: FindingsCopy;
  row: FindingRow;
  handlers: FindingsHandlers;
  onAnyway: (finding: DependencyFinding) => void;
}) {
  const { finding } = row;
  const where =
    finding.kind === 'secret'
      ? copy.where(finding.where.area, finding.where.file, finding.where.line)
      : copy.depWhere;
  const title =
    finding.kind === 'secret'
      ? ((finding.label && SECRET_TITLE(copy)[finding.label]) ?? copy.secretTitleUnknown)
      : formatCopy(finding.severity === 'unknown' ? copy.depTitleUnrated : copy.depTitle, {
          package: finding.package,
          version: finding.version,
          severity: severityWord(copy, finding),
        });
  const why = finding.kind === 'secret' ? copy.secretWhy : copy.depWhy;
  const advice = {
    secret: copy.secretDo,
    update: copy.depDoUpdate,
    'no-fix': copy.depDoNoFix,
    unrated: copy.depDoUnrated,
    warning: '',
  }[row.advice];
  return (
    <li
      className="pc-finding"
      data-testid="finding-row"
      data-holds={row.holds ? 'true' : 'false'}
      data-kind={finding.kind}
    >
      <div className="pc-finding-head">
        <StatusPill tone={TONE[finding.severity]}>{TAG(copy)[finding.severity]}</StatusPill>
        <strong>{title}</strong>
      </div>
      <p className="pc-finding-where">{where}</p>
      {finding.acknowledged ? (
        <p className="pc-finding-ack" data-testid="finding-ack">
          {formatCopy(copy.anywayDone, {
            name: finding.acknowledged.by,
            date: finding.acknowledged.at,
          })}
        </p>
      ) : (
        <>
          <p>{why}</p>
          {finding.kind === 'dependency' && finding.advisoryUrl && (
            <a href={finding.advisoryUrl} target="_blank" rel="noopener noreferrer">
              {copy.depViewAdvisory}
            </a>
          )}
          {advice && <p>{advice}</p>}
          {row.actions.length > 0 && (
            <div className="pc-finding-actions">
              {row.actions.includes('fix') && handlers.onFix && (
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={() => handlers.onFix?.(finding)}
                >
                  {copy.actionFix}
                </Button>
              )}
              {row.actions.includes('publish-anyway') &&
                handlers.onPublishAnyway &&
                finding.kind === 'dependency' && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    data-testid="publish-anyway"
                    onClick={() => onAnyway(finding)}
                  >
                    {copy.actionPublishAnyway}
                  </Button>
                )}
            </div>
          )}
        </>
      )}
    </li>
  );
}

/** The Checks list: blocking rows first, then the quiet warnings. Never a value, never raw scanner data. */
export function FindingsList({
  copy,
  report,
  handlers,
  lastChecked,
}: {
  copy: FindingsCopy;
  report: CheckReport;
  handlers: FindingsHandlers;
  lastChecked?: string | null;
}) {
  const presentation = presentFindings(report);
  const [confirming, setConfirming] = useState<DependencyFinding | null>(null);
  return (
    <section className="pc-findings" data-testid="findings">
      <FindingsSummary copy={copy} report={report} handlers={handlers} />
      {lastChecked && (
        <p className="pc-findings-time">{formatCopy(copy.lastChecked, { time: lastChecked })}</p>
      )}
      {presentation.rows.length > 0 && (
        <ul className="pc-findings-rows">
          {presentation.rows.map((row) => (
            <Row
              key={row.finding.id}
              copy={copy}
              row={row}
              handlers={handlers}
              onAnyway={setConfirming}
            />
          ))}
        </ul>
      )}
      {presentation.checkAgain &&
        presentation.primary !== 'check-again' &&
        presentation.summary.kind !== 'hidden' &&
        presentation.summary.kind !== 'held-private' &&
        handlers.onCheckAgain && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="findings-check-again"
            onClick={handlers.onCheckAgain}
          >
            {copy.actionCheckAgain}
          </Button>
        )}
      {confirming && (
        <PublishAnywaySheet
          copy={copy}
          finding={confirming}
          onConfirm={() => {
            const findingId = confirming.id;
            setConfirming(null);
            if (report.reportRef)
              handlers.onPublishAnyway?.({ findingId, reportRef: report.reportRef });
          }}
          onCancel={() => setConfirming(null)}
        />
      )}
    </section>
  );
}

/** The owner's confirm sheet: nothing is sent until the explicit confirm. */
export function PublishAnywaySheet({
  copy,
  finding,
  onConfirm,
  onCancel,
}: {
  copy: FindingsCopy;
  finding: DependencyFinding;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <AppDialog
      testId="publish-anyway-sheet"
      title={copy.anywayTitle}
      description={formatCopy(copy.anywayBody, {
        package: finding.package,
        version: finding.version,
        severity: severityWord(copy, finding),
      })}
      closeLabel={copy.anywayCancel}
      onClose={onCancel}
      footer={
        <div className="pc-app-dialog-actions">
          <Button type="button" variant="outline" size="sm" onClick={onCancel}>
            {copy.anywayCancel}
          </Button>
          <Button
            type="button"
            variant="default"
            size="sm"
            data-testid="publish-anyway-confirm"
            data-autofocus
            onClick={onConfirm}
          >
            {copy.anywayConfirm}
          </Button>
        </div>
      }
    >
      <></>
    </AppDialog>
  );
}

/**
 * The persisted notice line: ONLY the kind and count (what a chat message may store); no path, no finding, no
 * report. `onReview` is the host's navigation to its Checks surface (none exists yet: the host names it).
 */
export function FindingsNoticeLine({
  copy,
  notice,
  onReview,
}: {
  copy: FindingsCopy;
  notice: NonNullable<ChatNotice>;
  onReview?: () => void;
}) {
  const text = {
    'held-secret': copy.chatHeldSecret,
    'held-dependency': copy.chatHeldMany(1),
    'held-many': copy.chatHeldMany(notice.count),
    warnings: copy.chatWarnings(notice.count),
  }[notice.kind];
  return (
    <p className="pc-findings-notice" data-testid="findings-notice" role="status">
      {text}
      {onReview && (
        <>
          {' '}
          <Button type="button" variant="ghost" size="sm" onClick={onReview}>
            {copy.actionReview}
          </Button>
        </>
      )}
    </p>
  );
}

/** The App thread notice from a live report: the same counts-only line; nothing on a clean pass. */
export function FindingsChatNotice({
  copy,
  report,
  onReview,
}: {
  copy: FindingsCopy;
  report: CheckReport;
  onReview?: () => void;
}) {
  const notice = chatNoticeFor(presentFindings(report));
  return notice ? <FindingsNoticeLine copy={copy} notice={notice} onReview={onReview} /> : null;
}
